const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const FIRST_NAME_KEY = 'firstName';
const LAST_NAME_KEY = 'lastName';
const IDENTITY_KEY = 'identity';
const THEME_KEY = 'theme';
const GUEST_ID_KEY = 'avatar_guest_id';

export const getAccessToken = (): string | null =>
    localStorage.getItem(ACCESS_TOKEN_KEY);

export const getRefreshToken = (): string | null =>
    localStorage.getItem(REFRESH_TOKEN_KEY);

// Direct setter used by the silent-refresh flow so it can update only the
// access token without going through storeAuthSession (which resets theme
// and would clobber the existing refresh token when the BE returns null).
export const setAccessToken = (token: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
};

export const isLoggedIn = (): boolean => !!getAccessToken();

/**
 * The signed-in user's display name, built from the first/last name the auth
 * response stored in sessionStorage. Returns null for guests, and when only a
 * partial name is known it returns whichever half exists rather than a
 * half-empty string. Sent alongside the opaque `identity` on the chat request so
 * admin-side logs (noise events) can name the user instead of showing a raw id.
 */
export const getStoredUserName = (): string | null => {
    const first = (sessionStorage.getItem(FIRST_NAME_KEY) || '').trim();
    const last = (sessionStorage.getItem(LAST_NAME_KEY) || '').trim();
    const full = `${first} ${last}`.trim();
    return full || null;
};

export const getStoredValue = (key: string): string | null =>
    localStorage.getItem(key);

export interface AuthSessionContent {
    accessToken?: string;
    refreshToken?: string;
    firstName?: string;
    lastName?: string;
    identity?: string;
}

export const storeAuthSession = (content: AuthSessionContent | null | undefined): void => {
    if (!content) return;
    if (content.accessToken) localStorage.setItem(ACCESS_TOKEN_KEY, content.accessToken);
    if (content.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, content.refreshToken);
    // First/last name are personal data (CWE-312): keep them in sessionStorage
    // (cleared when the tab closes) rather than persisting to localStorage.
    if (content.firstName) sessionStorage.setItem(FIRST_NAME_KEY, content.firstName);
    if (content.lastName) sessionStorage.setItem(LAST_NAME_KEY, content.lastName);
    if (content.identity) localStorage.setItem(IDENTITY_KEY, content.identity);
    localStorage.setItem(THEME_KEY, 'light');
};

export const clearAuthStorage = (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    sessionStorage.removeItem(FIRST_NAME_KEY);
    sessionStorage.removeItem(LAST_NAME_KEY);
    localStorage.removeItem(IDENTITY_KEY);
    localStorage.removeItem(THEME_KEY);
    // avatar_guest_id intentionally preserved across logout
};

/**
 * A float in [0, 1) drawn from the CSPRNG, matching {@code Math.random}'s range
 * but without its predictability (CWE-330). WebCrypto is available in every
 * browser this app targets (and in the jsdom test env), so there is no
 * {@code Math.random} fallback.
 */
export const secureRandomUnit = (): number =>
    crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000;

const generateUuid = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (secureRandomUnit() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const getGuestId = (): string => {
    let id = localStorage.getItem(GUEST_ID_KEY);
    if (!id) {
        id = generateUuid();
        localStorage.setItem(GUEST_ID_KEY, id);
    }
    return id;
};

/**
 * Reads the currently-saved system language iso code from
 * {@code localStorage.languages.systemLanguage}. Falls back to {@code "en"}
 * if nothing's been picked yet (first session) or the value is unparseable.
 *
 * <p>Inlined here rather than imported from {@code system-language.ts} to
 * avoid the circular import: {@code system-language.ts} already depends on
 * {@code isRtlLanguage} defined below.
 */
const getStoredSystemLanguageIso = (): string => {
    try {
        const raw = localStorage.getItem('languages');
        if (!raw) return 'en';
        const parsed = JSON.parse(raw);
        const iso = (parsed?.systemLanguage ?? '').toString().trim();
        return iso || 'en';
    } catch {
        return 'en';
    }
};

/**
 * Headers attached to every outbound API call: auth (JWT or guest), plus
 * {@code Accept-Language} so back-end localization (e.g. FAQ copy) returns
 * the right locale for the user's chosen system language.
 */
export const getAuthHeaders = (): Record<string, string> => {
    const headers: Record<string, string> = {
        'Accept-Language': getStoredSystemLanguageIso(),
    };
    const token = getAccessToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    } else {
        headers['X-Guest-ID'] = getGuestId();
    }
    return headers;
};

/**
 * Languages whose script flows right-to-left. Used to drive the system
 * language RTL toggle and any text-direction decisions on the client.
 * ace-common does not currently expose direction in its language DTO, so
 * this set is the client-side source of truth.
 */
export const RTL_LANGUAGE_CODES: ReadonlySet<string> = new Set([
    'ar', 'he', 'fa', 'ur', 'ps', 'sd', 'ku', 'dv',
]);

/**
 * Strip the dialect/region suffix from a BCP-47 language tag.
 * Examples: {@code "ar-EG"} → {@code "ar"}, {@code "en-US"} → {@code "en"},
 * {@code "en"} → {@code "en"}, empty/undefined → {@code ""}.
 *
 * <p>Used when sending the language to the LLM (it expects a base code
 * only). The dialect-qualified value stays in the speech-recognition path.
 */
export const extractBaseLanguage = (iso: string | null | undefined): string =>
    (iso ?? '').split('-')[0].toLowerCase();

/**
 * Dialect-aware RTL check. {@code "ar-EG"} → true, {@code "en-US"} → false.
 */
export const isRtlLanguage = (iso: string | null | undefined): boolean =>
    RTL_LANGUAGE_CODES.has(extractBaseLanguage(iso));

const blobUrlCache = new Map<string, string>();

export const fetchAsBlobUrl = async (url: string): Promise<string> => {
    if (!url) return url;
    const cached = blobUrlCache.get(url);
    if (cached) return cached;

    const res = await fetch(url, { headers: getAuthHeaders() });
    if (!res.ok) {
        throw new Error(`Failed to fetch ${url}: ${res.status}`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    blobUrlCache.set(url, blobUrl);
    return blobUrl;
};
